/**
 * Exposure Apparel — customisations inside cart groups.
 *
 * Customisation fee lines (print locations added by the Custom builder) carry
 * `_base_variants`: the garment variants they apply to. This module:
 *   1. draws each customisation inside the garment group it belongs to
 *      (cart drawer + cart page) instead of as a separate group;
 *   2. keeps every customisation's quantity locked to the number of linked
 *      garments in the cart (changing sizes or removing the group follows);
 *   3. adds an "Add customisation" pop-up to each group, using the same
 *      locations and drawings as the product page (ea-print-placements.js);
 *   4. refreshes the drawer in place after any add (`cart:refresh`), so the
 *      cart never needs a page reload.
 *
 * Hooked from CartItems.groupCartItemsByColor() in cart.js via decorate().
 */
(function () {
  'use strict';

  // Same defaults as CUSTOMISATION_VARIANTS in custom-main-product.js
  const DEFAULT_VARIANTS = {
    'Digital Print': { Small: '54640950575395', Medium: '55708024471843', Large: '54640950608163', XL: '55708024504611' },
    Embroidery: { Small: '54640950640931', Large: '54640950673699', Cap: '55743133581603' },
  };
  const EMBROIDERY_MIN_UNITS = 10;
  // Product templates that use the apparel print builder — only these get "Add customisation"
  const BUILDER_TEMPLATES = ['custom', 'hats-beanies'];

  const variantsMap = () => window.CUSTOMISATION_VARIANTS || DEFAULT_VARIANTS;
  const placements = () => window.EAPrintPlacements;

  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  const spotFromLocation = (text) => {
    const spot = String(text || '').replace(/^Location\s*\d*\s*:\s*/i, '').trim();
    return placements() && placements().normaliseSpot ? placements().normaliseSpot(spot) : spot;
  };
  const conflictsWith = (a, b) => !!(placements() && placements().conflictsWith && placements().conflictsWith(a, b));

  function spotLabel(value) {
    const spot = placements() && placements().getSpot(value);
    return spot ? spot.label : value;
  }

  // ---------------------------------------------------------------------------
  // Variant prices (for the pop-up summary)
  // ---------------------------------------------------------------------------
  const priceCache = {};
  function variantPrice(id) {
    if (!priceCache[id]) {
      priceCache[id] = fetch(`/variants/${id}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .then((v) => (v ? v.price : null))
        .catch(() => null);
    }
    return priceCache[id];
  }

  // ---------------------------------------------------------------------------
  // Reading the cart DOM
  // ---------------------------------------------------------------------------
  function readCart(root) {
    const tbody = root.querySelector('tbody') || root.querySelector('#CartDrawer-Items-Body');
    if (!tbody) return null;
    const originals = Array.from(tbody.querySelectorAll('.cart-item--original'));
    const garments = originals.filter((r) => r.dataset.eaCustomisation !== 'true');
    const customisations = originals
      .filter((r) => r.dataset.eaCustomisation === 'true')
      .map((r) => ({
        key: r.dataset.lineKey,
        quantity: parseInt(r.dataset.quantity, 10) || 0,
        linePrice: parseInt(r.dataset.linePrice, 10) || 0,
        originalLinePrice: parseInt(r.dataset.originalLinePrice, 10) || 0,
        method: r.dataset.eaMethod || '',
        size: r.dataset.eaCustSize || '',
        spot: spotFromLocation(r.dataset.eaLocation),
        base: (r.dataset.eaBaseVariants || '').split(',').map((s) => s.trim()).filter(Boolean),
        artwork: r.dataset.eaArtwork || '',
      }));

    const qtyByVariant = {};
    garments.forEach((r) => {
      const id = String(r.dataset.variantId);
      qtyByVariant[id] = (qtyByVariant[id] || 0) + (parseInt(r.dataset.quantity, 10) || 0);
    });

    const groups = Array.from(tbody.querySelectorAll('.cart-item--grouped')).map((row) => {
      const color = row.dataset.color || 'default';
      const lines = garments.filter(
        (g) => String(g.dataset.productId) === String(row.dataset.productId) && (g.dataset.color || 'default') === color
      );
      const variantIds = Array.from(new Set(lines.map((g) => String(g.dataset.variantId))));
      return {
        row,
        color,
        productTitle: (row.querySelector('.cart-item__name') || {}).textContent?.trim() || '',
        template: (lines[0] && lines[0].dataset.eaTemplate) || '',
        handle: (lines[0] && lines[0].dataset.eaHandle) || '',
        variantIds,
        quantity: variantIds.reduce((sum, id) => sum + (qtyByVariant[id] || 0), 0),
        customisations: [],
      };
    });
    // The same product in several colours = several groups sharing one tier count
    groups.forEach((g) => {
      g.siblings = groups.filter((o) => String(o.row.dataset.productId) === String(g.row.dataset.productId));
      g.splitAcrossGroups = g.siblings.length > 1;
      g.productQty = g.siblings.reduce((sum, o) => sum + o.quantity, 0);
    });

    // A customisation belongs to every garment group (product + colour) it was added
    // for, and covers ALL garments in those groups — adding another size to the group
    // grows it, removing the group removes it.
    customisations.forEach((c) => {
      const base = Array.from(new Set(c.base));
      if (base.length) {
        c.groups = groups.filter((g) => base.some((id) => g.variantIds.includes(id)));
      } else {
        // No link data on the line (added some other way): adopt it only when the
        // cart has exactly one garment group; otherwise leave it unlinked and NEVER
        // change or remove it automatically.
        c.groups = groups.length === 1 ? [groups[0]] : [];
        c.unlinked = groups.length !== 1;
      }
      c.expected = c.groups.reduce((sum, g) => sum + g.quantity, 0);
      c.targetBase = Array.from(new Set(c.groups.flatMap((g) => g.variantIds))).sort();
      c.baseInSync = base.length === c.targetBase.length && base.every((id) => c.targetBase.includes(id));
      if (c.unlinked) {
        c.expected = c.quantity;
        c.baseInSync = true;
      }
      if (c.groups[0]) c.groups[0].customisations.push(c);
    });

    return { tbody, groups, customisations };
  }

  // ---------------------------------------------------------------------------
  // Rendering inside each group
  // ---------------------------------------------------------------------------
  function customisationRow(c, group) {
    const P = placements();
    const spot = P && P.getSpot(c.spot);
    const thumb = P && spot ? P.svg(c.spot, spot.view) : '';
    const unit = c.quantity > 0 ? Math.round(c.linePrice / c.quantity) : 0;
    const fullUnit = c.quantity > 0 ? Math.round(c.originalLinePrice / c.quantity) : 0;
    const price = fullUnit > unit
      ? `<s class="ea-cust__was">${money(fullUnit * c.expected)}</s> ${money(unit * c.expected)}`
      : money(unit * c.expected);
    const shared = c.groups && c.groups.length > 1
      ? ` · covers ${c.groups.map((g) => (g.color === 'default' ? g.productTitle : g.color)).join(' + ')}`
      : '';
    const overlap = group && group.customisations.find((o) => o !== c && conflictsWith(o.spot, c.spot));
    const lockTitle = c.unlinked
      ? 'Not linked to a garment group'
      : `Locked to the ${c.expected} garment${c.expected === 1 ? '' : 's'} this applies to`;
    return `
      <li class="ea-cust__item" data-line-key="${esc(c.key)}">
        ${thumb ? `<img class="ea-cust__thumb" src="${thumb}" alt="" width="40" height="50">` : ''}
        <div class="ea-cust__info">
          <span class="ea-cust__name">${esc(c.spot ? spotLabel(c.spot) : 'Customisation')}</span>
          <span class="ea-cust__meta">${esc(c.method)}${c.size ? ' · ' + esc(c.size) : ''} · <span class="ea-cust__qty" title="${esc(lockTitle)}">×${c.expected}</span>${esc(shared)}</span>
          ${overlap ? `<span class="ea-cust__overlap">Overlaps ${esc(spotLabel(overlap.spot))} — remove one</span>` : ''}
          ${artworkStatus(c)}
        </div>
        <span class="ea-cust__price">${price}</span>
        <button type="button" class="ea-cust__remove" data-ea-cust-remove="${esc(c.key)}" aria-label="Remove ${esc(spotLabel(c.spot))} customisation">
          <svg viewBox="0 0 18 17" width="10" height="10" aria-hidden="true" focusable="false"><path fill="currentColor" d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z"/></svg>
        </button>
      </li>`;
  }

  // Compact size steppers: show only the sizes in the cart; "+ Add size" reveals
  // the rest (remembered per group across re-renders).
  const expandedSizes = new Set();
  const groupKey = (group) => `${group.row.dataset.productId}-${group.color}`;

  function compactSizes(group) {
    const wrap = group.row.querySelector('.cart-item__size-quantities');
    if (!wrap) return;
    wrap.querySelectorAll('.ea-size-toggle').forEach((el) => el.remove());
    let zeros = 0;
    wrap.querySelectorAll('.cart-item__size-quantity').forEach((el) => {
      const input = el.querySelector('.quantity__input');
      const isZero = (parseInt(input && input.value, 10) || 0) === 0;
      el.classList.toggle('is-zero', isZero);
      if (isZero) zeros += 1;
      const label = el.querySelector('.size-label');
      if (label) label.textContent = label.textContent.replace(/:\s*$/, '');
    });
    const expanded = expandedSizes.has(groupKey(group));
    wrap.classList.toggle('is-expanded', expanded);
    if (!zeros) return;
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ea-size-toggle';
    toggle.dataset.eaSizeToggle = groupKey(group);
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = expanded ? 'Done' : '+ Add size';
    wrap.appendChild(toggle);
  }

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-ea-size-toggle]');
    if (!toggle) return;
    e.preventDefault();
    const key = toggle.dataset.eaSizeToggle;
    const wrap = toggle.closest('.cart-item__size-quantities');
    const expanded = !expandedSizes.has(key);
    if (expanded) expandedSizes.add(key);
    else expandedSizes.delete(key);
    wrap.classList.toggle('is-expanded', expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = expanded ? 'Done' : '+ Add size';
  });

  function renderGroup(group) {
    const canAdd = BUILDER_TEMPLATES.includes(group.template) && group.quantity > 0 && placements();
    if (!group.customisations.length && !canAdd) return;

    // Full-width cell under the group's sizes. In the drawer (and on small screens) the
    // grouped row is a CSS grid, so the cell spans it; where the row is a real table row
    // (cart page on desktop) the cell goes in its own row directly underneath.
    const cell = document.createElement('td');
    cell.className = 'ea-cust-cell';
    cell.setAttribute('role', 'cell');
    // Span only the columns the row actually shows: counting hidden cells would add
    // a phantom column to the cart table and squash the header.
    const columns = [...group.row.children]
      .filter((el) => getComputedStyle(el).display !== 'none')
      .reduce((n, el) => n + (el.colSpan || 1), 0);
    cell.colSpan = Math.max(columns, 1);

    const block = document.createElement('div');
    block.className = 'ea-cust';
    block.innerHTML = `
      ${group.customisations.length ? `
        <p class="ea-cust__heading">Customisations</p>
        <ul class="ea-cust__list" role="list">${group.customisations.map((c) => customisationRow(c, group)).join('')}</ul>` : ''}
      ${canAdd ? `
        <button type="button" class="ea-cust__add" data-ea-cust-add>
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          Add customisation
        </button>` : ''}`;

    cell.appendChild(block);
    if (getComputedStyle(group.row).display === 'table-row') {
      const tr = document.createElement('tr');
      tr.className = 'ea-cust-row';
      tr.setAttribute('role', 'row');
      tr.appendChild(cell);
      group.row.insertAdjacentElement('afterend', tr);
    } else {
      group.row.appendChild(cell);
    }
    block._eaGroup = group;
  }

  // Customisation lines we can't tie to a garment group (no link data and several
  // groups in the cart): listed on their own so they're visible and removable.
  function renderUnlinked(state) {
    const unlinked = state.customisations.filter((c) => c.unlinked);
    if (!unlinked.length) return;
    const lastGroup = state.groups[state.groups.length - 1];
    const tr = document.createElement('tr');
    tr.className = 'ea-cust-row ea-cust-row--unlinked';
    tr.setAttribute('role', 'row');
    const cell = document.createElement('td');
    cell.className = 'ea-cust-cell';
    cell.colSpan = 5;
    cell.innerHTML = `
      <div class="ea-cust">
        <p class="ea-cust__heading">Other customisations</p>
        <ul class="ea-cust__list" role="list">${unlinked.map((c) => customisationRow(c, null)).join('')}</ul>
      </div>`;
    tr.appendChild(cell);
    if (lastGroup) lastGroup.row.insertAdjacentElement('afterend', tr);
    else state.tbody.appendChild(tr);
  }

  // ---------------------------------------------------------------------------
  // Cart mutations (no page reloads — re-render sections in place)
  // ---------------------------------------------------------------------------
  function cartRoots() {
    return Array.from(document.querySelectorAll('cart-drawer-items, cart-items'));
  }

  async function refreshFrom(cart, preferredRoot) {
    const root = preferredRoot && preferredRoot.isConnected ? preferredRoot : cartRoots()[0];
    if (root && typeof root.refreshCartSections === 'function') {
      await root.refreshCartSections(cart);
    } else {
      await renderDrawer(cart, false);
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ea-cart-customisations', cartData: cart });
    }
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const message = (data && (data.description || data.message)) || 'Something went wrong. Please try again.';
      throw new Error(message);
    }
    return data;
  }

  let syncing = false;
  async function syncQuantities(state, root) {
    if (syncing) return;
    const pending = state.customisations.filter((c) => c.key && (c.quantity !== c.expected || !c.baseInSync));
    if (!pending.length) return;
    syncing = true;
    document.querySelectorAll('cart-drawer-items, cart-items').forEach((el) => el.classList.add('ea-cust-syncing'));
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      let latest = cart;
      for (const c of pending) {
        const line = cart.items.find((i) => i.key === c.key);
        if (!line) continue;
        latest = await postJSON(
          '/cart/change.js',
          c.expected > 0
            ? { id: c.key, quantity: c.expected, properties: { ...line.properties, _base_variants: c.targetBase.join(',') } }
            : { id: c.key, quantity: 0 }
        );
      }
      await refreshFrom(latest, root);
    } catch (e) {
      console.warn('[EA customisations] quantity sync failed', e);
    } finally {
      setTimeout(() => { syncing = false; }, 400);
      document.querySelectorAll('.ea-cust-syncing').forEach((el) => el.classList.remove('ea-cust-syncing'));
    }
  }

  // Bring customisation lines in line with their garment groups using the cart JSON,
  // BEFORE the cart is re-rendered — so every action renders once, already correct.
  const colourOf = (item) => {
    const opt = (item.options_with_values || []).find((o) => /^colou?r$/i.test(o.name));
    return opt ? opt.value : 'default';
  };
  const isCustomisationItem = (item) =>
    !!(item.properties && item.properties._customisation_method) || item.handle === 'customisation';

  async function reconcile(cart) {
    if (!cart || !Array.isArray(cart.items) || syncing) return cart;
    const garments = cart.items.filter((i) => !isCustomisationItem(i));
    const groups = {};
    garments.forEach((i) => {
      const key = `${i.product_id}-${colourOf(i)}`;
      groups[key] = groups[key] || { variantIds: new Set(), quantity: 0 };
      groups[key].variantIds.add(String(i.variant_id));
      groups[key].quantity += i.quantity;
    });
    const groupList = Object.values(groups);

    const changes = [];
    cart.items.filter(isCustomisationItem).forEach((c) => {
      const base = String((c.properties && c.properties._base_variants) || '').split(',').map((s) => s.trim()).filter(Boolean);
      let linked;
      if (base.length) linked = groupList.filter((g) => base.some((id) => g.variantIds.has(id)));
      else if (groupList.length === 1) linked = groupList;
      else return; // unlinked: never touched automatically
      const expected = linked.reduce((sum, g) => sum + g.quantity, 0);
      const target = Array.from(new Set(linked.flatMap((g) => Array.from(g.variantIds)))).sort();
      const baseInSync = base.length === target.length && base.every((id) => target.includes(id));
      if (c.quantity !== expected || !baseInSync) {
        changes.push(
          expected > 0
            ? { id: c.key, quantity: expected, properties: { ...(c.properties || {}), _base_variants: target.join(',') } }
            : { id: c.key, quantity: 0 }
        );
      }
    });
    if (!changes.length) return cart;

    syncing = true;
    try {
      let latest = cart;
      for (const change of changes) latest = await postJSON('/cart/change.js', change);
      return latest;
    } catch (e) {
      console.warn('[EA customisations] reconcile failed', e);
      return cart;
    } finally {
      setTimeout(() => { syncing = false; }, 400);
    }
  }

  function decorate(root) {
    if (!root) return;
    const state = readCart(root);
    if (!state) return;
    state.tbody.querySelectorAll('.ea-cust-row, .ea-cust-cell').forEach((el) => el.remove());
    state.groups.forEach((group) => {
      compactSizes(group);
      renderGroup(group);
    });
    renderUnlinked(state);
    root.classList.add('ea-grouped');
    syncQuantities(state, root);
  }

  // ---------------------------------------------------------------------------
  // Artwork per customisation: "Artwork missing" + upload, or attached + replace.
  // Optional — checkout never requires it.
  // ---------------------------------------------------------------------------
  const ARTWORK_MAX_BYTES = 20 * 1024 * 1024;
  const ARTWORK_ACCEPT = '.png,.jpg,.jpeg,.pdf,.ai,.eps,.svg';

  function artworkStatus(c) {
    if (!c.key) return '';
    if (c.artwork) {
      const name = decodeURIComponent(String(c.artwork).split('/').pop().split('?')[0] || 'Artwork');
      return `
        <span class="ea-cust__art ea-cust__art--ok">
          <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false"><path fill="currentColor" d="M6.2 11.6 2.9 8.3l1-1 2.3 2.3 5.9-5.9 1 1z"/></svg>
          <a href="${esc(c.artwork)}" target="_blank" rel="noopener" title="${esc(name)}">Artwork attached</a>
          <button type="button" class="ea-cust__art-btn" data-ea-art-upload="${esc(c.key)}">Replace</button>
        </span>`;
    }
    return `
      <span class="ea-cust__art ea-cust__art--missing">
        <span class="ea-cust__art-flag">Artwork needed</span>
        <button type="button" class="ea-cust__art-btn" data-ea-art-upload="${esc(c.key)}">Upload</button>
      </span>`;
  }

  async function uploadArtwork(key, file, btn) {
    const item = btn.closest('.ea-cust__item');
    const status = item && item.querySelector('.ea-cust__art');
    if (file.size > ARTWORK_MAX_BYTES) {
      if (status) status.insertAdjacentHTML('beforeend', '<span class="ea-cust__art-error">File is over 20 MB</span>');
      return;
    }
    item && item.classList.add('is-uploading');
    btn.disabled = true;
    btn.textContent = 'Uploading';
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const line = cart.items.find((i) => i.key === key);
      if (!line) throw new Error('Line not found');
      // Line item files can only be attached when a line is added, so the
      // customisation is re-added with the file and the old line removed.
      const formData = new FormData();
      formData.append('id', line.variant_id);
      formData.append('quantity', line.quantity);
      Object.entries(line.properties || {}).forEach(([k, v]) => {
        if (k !== 'Artwork' && v != null) formData.append(`properties[${k}]`, v);
      });
      formData.append('properties[Artwork]', file, file.name);
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      if (!res.ok) throw new Error('Upload failed');
      const updated = await postJSON('/cart/change.js', { id: key, quantity: 0 });
      await refreshFrom(updated, btn.closest('cart-drawer-items, cart-items'));
    } catch (err) {
      console.warn('[EA customisations] artwork upload failed', err);
      item && item.classList.remove('is-uploading');
      btn.disabled = false;
      btn.textContent = 'Try again';
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ea-art-upload]');
    if (!btn) return;
    e.preventDefault();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ARTWORK_ACCEPT;
    input.addEventListener('change', () => {
      if (input.files && input.files[0]) uploadArtwork(btn.dataset.eaArtUpload, input.files[0], btn);
    });
    input.click();
  });

  // Additional information (cart attributes) — saved as the customer types
  const attributeTimers = {};
  document.addEventListener('input', (e) => {
    const field = e.target.closest('[data-ea-cart-attribute]');
    if (!field) return;
    const name = field.dataset.eaCartAttribute;
    const status = field.parentElement.querySelector('[data-ea-cart-attribute-status]');
    if (status) status.textContent = '';
    clearTimeout(attributeTimers[name]);
    attributeTimers[name] = setTimeout(async () => {
      try {
        await postJSON('/cart/update.js', { attributes: { [name]: field.value } });
        if (status) status.textContent = 'Saved';
        const added = field.closest('.ea-cart-info')?.querySelector('[data-ea-cart-attribute-added]');
        if (added) added.hidden = !field.value.trim();
      } catch (err) {
        if (status) status.textContent = 'Not saved, please try again';
      }
    }, 600);
  });

  // Re-draw thumbnails once the placement logo has loaded
  document.addEventListener('ea:placement-logo-ready', () => {
    cartRoots().forEach((root) => {
      if (root.querySelector('.cart-item--grouped')) decorate(root);
    });
  });

  // Remove a single customisation
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-ea-cust-remove]');
    if (!btn) return;
    e.preventDefault();
    const item = btn.closest('.ea-cust__item');
    item && item.classList.add('is-removing');
    btn.disabled = true;
    try {
      const cart = await postJSON('/cart/change.js', { id: btn.dataset.eaCustRemove, quantity: 0 });
      await refreshFrom(cart, btn.closest('cart-drawer-items, cart-items'));
    } catch (err) {
      item && item.classList.remove('is-removing');
      btn.disabled = false;
      console.warn('[EA customisations] remove failed', err);
    }
  });

  // Remove a whole garment group together with the customisations that only belong
  // to it, in ONE cart update (runs before cart.js's own group-remove handler).
  document.addEventListener(
    'click',
    async (e) => {
      const btn = e.target.closest('.ea-cart-group-remove');
      const row = btn && btn.closest('.cart-item--grouped');
      const root = row && row.closest('cart-drawer-items, cart-items');
      if (!root) return;
      const state = readCart(root);
      const group = state && state.groups.find((g) => g.row === row);
      if (!group) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const updates = {};
      state.tbody.querySelectorAll('.cart-item--original').forEach((r) => {
        if (
          r.dataset.eaCustomisation !== 'true' &&
          String(r.dataset.productId) === String(row.dataset.productId) &&
          (r.dataset.color || 'default') === group.color &&
          r.dataset.lineKey
        ) {
          updates[r.dataset.lineKey] = 0;
        }
      });
      state.customisations.forEach((c) => {
        if (c.groups.length === 1 && c.groups[0] === group) updates[c.key] = 0;
      });

      row.classList.add('is-removing');
      btn.disabled = true;
      try {
        const cart = await postJSON('/cart/update.js', { updates });
        await refreshFrom(cart, root);
      } catch (err) {
        row.classList.remove('is-removing');
        btn.disabled = false;
        console.warn('[EA customisations] group remove failed', err);
      }
    },
    true
  );

  // ---------------------------------------------------------------------------
  // "Add customisation" pop-up
  // ---------------------------------------------------------------------------
  let modal = null;
  let ctx = null;

  function buildModal() {
    modal = document.createElement('div');
    modal.className = 'ea-cust-modal';
    modal.hidden = true;
    modal.innerHTML = `
      <div class="ea-cust-modal__overlay" data-ea-cust-close></div>
      <div class="ea-cust-modal__panel" role="dialog" aria-modal="true" aria-labelledby="EaCustModalTitle" tabindex="-1">
        <button type="button" class="ea-cust-modal__close" data-ea-cust-close aria-label="Close">
          <svg viewBox="0 0 18 17" width="14" height="14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z"/></svg>
        </button>
        <h2 class="ea-cust-modal__title" id="EaCustModalTitle">Add a customisation</h2>
        <p class="ea-cust-modal__sub" data-ea-cust-sub></p>

        <p class="ea-cust-modal__label">Printing method</p>
        <div class="ea-cust-modal__methods" data-ea-cust-methods></div>
        <p class="ea-cust-modal__note" data-ea-cust-method-note hidden></p>

        <p class="ea-cust-modal__label">Area</p>
        <div class="ea-cust-modal__areas" role="tablist" data-ea-cust-areas></div>

        <div class="ea-cust-modal__grid" data-ea-cust-grid></div>

        <div class="ea-cust-modal__footer">
          <div class="ea-cust-modal__summary" data-ea-cust-summary aria-live="polite"></div>
          <p class="ea-cust-modal__error" data-ea-cust-error role="alert" hidden></p>
          <div class="ea-cust-modal__actions">
            <button type="button" class="ea-cust-modal__cancel" data-ea-cust-close>Cancel</button>
            <button type="button" class="ea-cust-modal__submit" data-ea-cust-submit disabled>Add to order</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(modal);

    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-ea-cust-close]')) return closeModal();
      const method = e.target.closest('[data-ea-cust-method]');
      if (method && !method.disabled) {
        ctx.method = method.dataset.eaCustMethod;
        return renderModal();
      }
      const area = e.target.closest('[data-ea-cust-area]');
      if (area) {
        ctx.area = area.dataset.eaCustArea;
        ctx.spot = null;
        return renderModal();
      }
      const tile = e.target.closest('[data-ea-cust-spot]');
      if (tile && !tile.disabled) {
        ctx.spot = tile.dataset.eaCustSpot;
        return renderModal();
      }
      if (e.target.closest('[data-ea-cust-submit]')) submitModal();
    });
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal();
    });
  }

  const METHOD_ICONS = {
    'Digital Print': '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 9V3.5h10V9"/><rect x="3" y="9" width="18" height="8" rx="2"/><path d="M7 14h10v6.5H7z"/><circle cx="17.5" cy="12" r="0.4" fill="currentColor"/></svg>',
    Embroidery: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.5 3.5 6 18l-2 2.5L6.5 18.5"/><ellipse cx="18.6" cy="5.4" rx="1.6" ry="0.7" transform="rotate(-45 18.6 5.4)"/><path d="M18.6 5.4c-4.5-2.2-8.6 1.6-5.6 4.6s1.5 7.4-4.4 7.1"/></svg>',
  };

  function openModal(group, root) {
    if (!placements()) return;
    if (!modal) buildModal();
    ctx = {
      group,
      root,
      method: placements().hatKindOf && placements().hatKindOf(group.handle) ? 'Embroidery' : 'Digital Print',
      area: 'Front',
      spot: null,
      taken: group.customisations.map((c) => c.spot),
      returnFocus: document.activeElement,
    };
    modal.hidden = false;
    document.documentElement.classList.add('ea-cust-modal-open');
    renderModal();
    requestAnimationFrame(() => modal.querySelector('.ea-cust-modal__panel').focus());
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove('ea-cust-modal-open');
    if (ctx && ctx.returnFocus && ctx.returnFocus.isConnected) ctx.returnFocus.focus();
    ctx = null;
  }

  async function renderModal() {
    const P = placements();
    const g = ctx.group;
    const isHat = !!(P.hatKindOf && P.hatKindOf(g.handle));
    const noun = isHat ? 'hat' : 'garment';
    // Hats: one embroidery covers every colour of the style in the cart, and the 10-hat minimum counts them all
    const coverQty = isHat ? g.productQty || g.quantity : g.quantity;
    const embroideryBlocked = coverQty < EMBROIDERY_MIN_UNITS;
    // Hats are only ever embroidered; garments fall back to a print when there are under 10
    if (embroideryBlocked && ctx.method === 'Embroidery' && !isHat) ctx.method = 'Digital Print';

    const who = g.color && g.color !== 'default' ? `${g.productTitle} · ${g.color}` : g.productTitle;
    modal.querySelector('[data-ea-cust-sub]').textContent =
      isHat
        ? `${g.productTitle} — applies to all ${coverQty} hat${coverQty === 1 ? '' : 's'} of this style in your cart.`
        : `${who} — applies to all ${g.quantity} ${noun}${g.quantity === 1 ? '' : 's'} in this group.`;

    modal.querySelector('[data-ea-cust-methods]').innerHTML = [
      { id: 'Digital Print', pill: 'Cost effective', pillClass: 'cost', icon: METHOD_ICONS['Digital Print'], hide: isHat },
      { id: 'Embroidery', pill: 'Premium', pillClass: 'premium', blocked: embroideryBlocked, icon: METHOD_ICONS.Embroidery },
    ].filter((m) => !m.hide)
      .map((m) => `
        <button type="button" class="ea-cust-modal__method${ctx.method === m.id ? ' is-selected' : ''}" data-ea-cust-method="${m.id}" aria-pressed="${ctx.method === m.id}"${m.blocked ? ' disabled' : ''}>
          <span class="ea-cust-modal__pill ea-cust-modal__pill--${m.pillClass}">${m.pill}</span>
          <span class="ea-cust-modal__method-icon">${m.icon}</span>
          <span class="ea-cust-modal__method-name">${m.id}</span>
          ${m.id === 'Embroidery' ? `<span class="ea-cust-modal__method-sub">Min. ${EMBROIDERY_MIN_UNITS} units</span>` : ''}
        </button>`)
      .join('');

    const note = modal.querySelector('[data-ea-cust-method-note]');
    note.hidden = !embroideryBlocked;
    note.textContent = embroideryBlocked
      ? `Embroidery needs at least ${EMBROIDERY_MIN_UNITS} ${noun}s — you have ${coverQty}.`
      : '';

    // Only the print positions Tapstitch offers on this blank (e.g. no sleeves on most fleece)
    const areas = P.areasFor ? P.areasFor(g.handle) : P.AREAS;
    if (!areas.some((a) => a.id === ctx.area)) ctx.area = areas[0].id;
    modal.querySelector('[data-ea-cust-areas]').innerHTML = areas.map((a) => `
      <button type="button" role="tab" class="ea-cust-modal__area${ctx.area === a.id ? ' is-selected' : ''}" data-ea-cust-area="${a.id}" aria-selected="${ctx.area === a.id}">${a.id}</button>`).join('');

    const areaTabs = modal.querySelector('[data-ea-cust-areas]');
    if (areaTabs) areaTabs.hidden = areas.length < 2; // hats have a single area, so no tabs
    const area = areas.find((a) => a.id === ctx.area) || areas[0];
    modal.querySelector('[data-ea-cust-grid]').innerHTML = area.spots.map((s) => {
      const taken = ctx.taken.includes(s.value);
      const blockedBy = !taken && ctx.taken.find((t) => conflictsWith(t, s.value));
      const selected = ctx.spot === s.value;
      return `
        <button type="button" class="ea-cust-tile${selected ? ' is-selected' : ''}${taken || blockedBy ? ' is-taken' : ''}" data-ea-cust-spot="${esc(s.value)}" aria-pressed="${selected}"${taken || blockedBy ? ' disabled' : ''}${blockedBy ? ` title="Overlaps ${esc(spotLabel(blockedBy))}"` : ''}>
          <span class="ea-cust-tile__badge">${taken ? 'Added' : blockedBy ? 'Overlaps' : isHat ? 'Embroidered' : P.getSize(s.value, undefined, g.handle)}</span>
          <img src="${P.svg(s.value, area.view, P.garmentOf ? P.garmentOf(g.handle) : null, g.handle)}" alt="" width="160" height="200">
          <span class="ea-cust-tile__label">${esc(s.label)}</span>
        </button>`;
    }).join('');

    const submit = modal.querySelector('[data-ea-cust-submit]');
    const summary = modal.querySelector('[data-ea-cust-summary]');
    modal.querySelector('[data-ea-cust-error]').hidden = true;
    submit.disabled = !ctx.spot || (isHat && embroideryBlocked);

    if (!ctx.spot) {
      summary.innerHTML = `<span class="ea-cust-modal__hint">Choose where the ${isHat ? 'embroidery' : 'print'} goes.</span>`;
      return;
    }
    const size = P.getSize(ctx.spot, ctx.method, ctx.group && ctx.group.handle);
    const variantId = (variantsMap()[ctx.method] || {})[size];
    const label = spotLabel(ctx.spot);
    const sizeText = size === 'Cap' ? '' : ` (${size})`;
    summary.innerHTML = `<span><strong>${esc(label)}</strong> · ${esc(ctx.method)}${sizeText}</span>`;
    const unit = variantId ? await variantPrice(variantId) : null;
    if (ctx && ctx.spot && spotLabel(ctx.spot) === label && unit != null) {
      summary.innerHTML = `<span><strong>${esc(label)}</strong> · ${esc(ctx.method)}${sizeText}</span>
        <span class="ea-cust-modal__price">${money(unit)} × ${coverQty} = <strong>${money(unit * coverQty)}</strong></span>`;
    }
  }

  async function submitModal() {
    const P = placements();
    if (!ctx || !ctx.spot) return;
    const g = ctx.group;
    const isHat = !!(P.hatKindOf && P.hatKindOf(g.handle));
    // A hat's embroidery covers every colour of the style in the cart
    const covered = isHat && g.siblings && g.siblings.length ? g.siblings : [g];
    const coveredQty = covered.reduce((sum, o) => sum + o.quantity, 0);
    const coveredVariantIds = Array.from(new Set(covered.flatMap((o) => o.variantIds)));
    const size = P.getSize(ctx.spot, ctx.method, ctx.group && ctx.group.handle);
    const variantId = (variantsMap()[ctx.method] || {})[size];
    const submit = modal.querySelector('[data-ea-cust-submit]');
    const error = modal.querySelector('[data-ea-cust-error]');
    if (!variantId) {
      error.textContent = 'This customisation is not available right now.';
      error.hidden = false;
      return;
    }
    submit.disabled = true;
    submit.classList.add('is-loading');
    try {
      await postJSON('/cart/add.js', {
        items: [{
          id: Number(variantId),
          quantity: coveredQty,
          properties: {
            _customisation_method: ctx.method,
            _customisation_size: size,
            _linked_to_base_product: 'true',
            _print_location: `Location ${g.customisations.length + 1}: ${ctx.spot}`,
            _base_variants: coveredVariantIds.join(','),
            _location_details: `${ctx.spot} (${ctx.method}, ${size})`,
          },
        }],
      });
      const cart = await fetch('/cart.js').then((r) => r.json());
      const root = ctx.root;
      closeModal();
      await refreshFrom(cart, root);
    } catch (err) {
      error.textContent = err.message;
      error.hidden = false;
      submit.disabled = false;
    } finally {
      submit.classList.remove('is-loading');
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ea-cust-add]');
    if (!btn) return;
    e.preventDefault();
    const block = btn.closest('.ea-cust');
    if (block && block._eaGroup) openModal(block._eaGroup, btn.closest('cart-drawer-items, cart-items'));
  });

  // ---------------------------------------------------------------------------
  // Smooth adds: product-page add buttons dispatch `cart:refresh` — refresh the
  // drawer / cart page in place and open the drawer instead of needing a reload.
  // ---------------------------------------------------------------------------
  async function renderDrawer(cart, open) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return;
    if (cart && cart.item_count > 0) drawer.classList.remove('is-empty');
    if (open) {
      await drawer.renderContents({ id: cart ? cart.token : Date.now() });
      return;
    }
    const parsed = await drawer.fetchSections({ id: cart ? cart.token : Date.now() });
    drawer.getSectionsToRender().forEach((section) => {
      const html = parsed.sections && parsed.sections[section.id];
      if (!html) return;
      const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const inner = drawer.getSectionInnerHTML(html, section.selector);
      if (el && inner != null) el.innerHTML = inner;
    });
  }

  let refreshTimer = null;
  document.addEventListener('cart:refresh', () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      try {
        const cart = await reconcile(await fetch('/cart.js').then((r) => r.json()));
        const onCartPage = window.location.pathname.replace(/\/$/, '').endsWith('/cart');
        const page = document.querySelector('cart-items');
        if (onCartPage && page && typeof page.refreshCartSections === 'function') {
          await page.refreshCartSections(cart);
        } else {
          await renderDrawer(cart, true);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ea-cart-refresh', cartData: cart });
        }
      } catch (err) {
        console.warn('[EA customisations] cart refresh failed', err);
      }
    }, 120);
  });

  window.EACartCustomisations = { decorate, reconcile, open: openModal };

  // Decorate any cart already grouped before this script loaded
  cartRoots().forEach((root) => {
    if (root.querySelector('.cart-item--grouped')) decorate(root);
  });
})();
